<template>
	<Layout>
		<ModalMomentDownload
			:modal-name="modalNameMomentDownload"
		/>
		<ModalConfirm
			:modal-name="modalNameMomentDelete"
			:title="$I18n.trans('commons.type_delete', {'type': $I18n.trans_choice('commons.moment', 1)})"
			:text="$I18n.trans('event.multiformat_editor.messages.confirm_delete')"
			:confirm-button-text="$Helpers.ucfirst($I18n.trans('commons.delete'))"
			@confirmed="handleMomentDeleteConfirm"
		/>
		<template v-if="!isMobileViewport">
			<JumbotronCarousel
				v-if="slides.length > 0"
				:slides="slides"
			>
				<template #after>
					<HeaderSponsors
						v-if="headerSponsors"
						:sponsors="headerSponsors"
					/>
				</template>
			</JumbotronCarousel>
			<Jumbotron
				v-else
				:bg-image-url="bgImageUrl"
				:main-image-url="mainImageUrl"
				:title="title"
				:special-announcement="false"
			>
				<template #after>
					<HeaderSponsors
						v-if="headerSponsors"
						:sponsors="headerSponsors"
					/>
				</template>
			</Jumbotron>
		</template>
		<FeedNavigationDesktop />
		<div class="moments l-container">
			<section
				v-if="$arenaConfig.isSwissLeague"
				class="moments-introduction"
				aria-labelledby="moments-introduction-title"
			>
				<h2
					id="moments-introduction-title"
					class="moments-introduction__title"
				>
					{{ $I18n.trans('arena.moments.introduction.title') }}
				</h2>
				<p class="moments-introduction__description">
					{{ $I18n.trans('arena.moments.introduction.description') }}
				</p>
			</section>
			<LoadingSpinner
				v-if="isLoading"
				class="moments__loading-spinner"
			/>
			<div
				v-else-if="videos.items.length === 0"
				class="moments__instructions"
				:class="[
					{
						'is-mobile': isMobileViewport,
					},
				]"
			>
				<div
					v-for="(instruction, index) in instructions"
					:key="index"
					class="moments__instruction-item"
				>
					<div class="moments__instruction-number">
						0{{ index + 1 }}
					</div>
					<div class="moments__instruction-content">
						<h3 class="moments__instruction-title">
							{{ instruction.title }}
							<V2Icon
								v-if="index === 0"
								icon-name="formatconverter"
								size="md"
							/>
						</h3>
						<p class="moments__instruction-text">
							{{ instruction.text }}
						</p>
					</div>
				</div>
			</div>
			<div class="moments__list">
				<MomentTile
					v-for="moment in videos.items"
					:key="moment.id"
					v-bind="momentTileProps(moment)"
					:data-video-id="moment.id"
					:name="moment.event.name"
					:can-delete="true"
					@download="handleDownloadMoment(moment)"
					@delete="handleDeleteMoment(moment)"
				/>
			</div>

			<div
				v-if="!isMobileViewport && videos.pagination.total > 0"
				class="moments__pagination"
			>
				<Pagination
					:disabled="isLoading"
					:total="videos.pagination.total"
					:per-page="videos.pagination.perPage"
					:current-page="videos.pagination.currentPage"
					:last-page="videos.pagination.lastPage"
					:show-pager="false"
					:dark-theme="!$arenaConfig.isLightTheme"
					@change="handlePaginationChange"
				/>
			</div>

			<div
				v-if="isMobileViewport && videos.pagination.currentPage < videos.pagination.lastPage"
				ref="loadMoreTrigger"
				class="moments__load-more-trigger"
			/>
		</div>
	</Layout>
</template>

<script>
import Layout from '@js/Views/Layout';
import Jumbotron from '@js/components/Jumbotron';
import JumbotronCarousel from '@js/components/JumbotronCarousel';
import HeaderSponsors from '@js/components/HeaderSponsors';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import MomentTile from '@shared/components/MomentTile/MomentTile';
import FeedNavigationDesktop from '@js/components/FeedNavigationDesktop';
import ModalConfirm from '@shared/components/ModalConfirm';
import ModalMomentDownload from '@shared/components/MomentTile/ModalMomentDownload';
import V2Icon from '@shared/components/Icon';
import { momentTileProps } from '@shared/components/MomentTile/momentTileProps';
import { AdType } from '@shared/enums/AdType';
import { mapGetters } from 'vuex';
import Pagination from '@shared/components/Pagination';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'ViewMoments',
	components: {
		Layout,
		Jumbotron,
		JumbotronCarousel,
		HeaderSponsors,
		LoadingSpinner,
		MomentTile,
		FeedNavigationDesktop,
		ModalMomentDownload,
		ModalConfirm,
		Pagination,
		V2Icon,
	},
	props: {
		bgImageUrl: {
			type:    String,
			default: null,
		},
		mainImageUrl: {
			type:    String,
			default: null,
		},
		title: {
			type:    String,
			default: null,
		},
		slides: {
			type:    Array,
			default: () => [],
		},
		ads: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			videos: {
				items:      [],
				pagination: {
					currentPage: 1,
					perPage:     10,
					lastPage:    1,
					total:       0,
				},
			},
			isLoading:               false,
			observer:                null,
			momentTileProps,
			modalNameMomentDownload: 'momentDownloadModal',
			modalNameMomentDelete:   'momentDeleteModal',
		};
	},
	computed: {
		...mapGetters({
			isMobileViewport:     'device/isMobileViewport',
			userId:               'user/userId',
			profileIsInitialized: 'user/isInitialized',
		}),
		headerSponsors() {
			return this.ads.find(ad => ad.type === AdType.SPONSORS_HEADER)?.logos;
		},
		instructions() {
			return [
				{
					title: this.$I18n.trans('arena.moments.instructions.choose_scenes'),
					text:  this.$I18n.trans('arena.moments.instructions.scenes_description'),
				},
				{
					title: this.$I18n.trans('arena.moments.instructions.create_clip'),
					text:  this.$I18n.trans('arena.moments.instructions.clip_description'),
				},
				{
					title: this.$I18n.trans('arena.moments.instructions.share_moment'),
					text:  this.$I18n.trans('arena.moments.instructions.moment_description'),
				},
			];
		},
	},
	watch: {
		isMobileViewport(newValue) {
			if (newValue) {
				this.setupIntersectionObserver();
			} else if (this.observer) {
				this.observer.disconnect();
				this.observer = null;
			}
		},
		profileIsInitialized() {
			this.fetchVideos(1, false);
		},
	},
	mounted() {
		if (this.isMobileViewport) {
			this.setupIntersectionObserver();
		}
	},
	beforeDestroy() {
		if (this.observer) {
			this.observer.disconnect();
		}
	},
	methods: {
		async fetchVideos(page = 1, append = false) {
			if (this.isLoading) return;

			this.isLoading = true;
			try {
				const searchParams = new URLSearchParams();
				searchParams.append('page', JSON.stringify(page));
				searchParams.append('perPage', JSON.stringify(this.videos.pagination.perPage));
				searchParams.append('relations', 'event');
				searchParams.append('filters', JSON.stringify({ user_id: this.userId }));

				const response = await httpClient.getEventVideoCrops(Object.fromEntries(searchParams));
				const data = response.data;

				// Store the last item's position before adding new items
				const lastItem = append ? this.videos.items[this.videos.items.length - 1] : null;
				const lastItemPosition = lastItem ? document.querySelector(`[data-video-id="${lastItem.id}"]`)?.getBoundingClientRect().top : null;

				if (append) {
					this.videos.items = [...this.videos.items, ...data.data];
				} else {
					this.videos.items = data.data;
				}
				this.videos.pagination = {
					...data.meta,
					perPage: 10,
				};

				// Restore scroll position after new items are added
				if (append && lastItemPosition !== null) {
					this.$nextTick(() => {
						const newLastItem = document.querySelector(`[data-video-id="${lastItem.id}"]`);
						if (newLastItem) {
							const newPosition = newLastItem.getBoundingClientRect().top;
							window.scrollBy(0, newPosition - lastItemPosition);
						}
					});
				}

				// Set up observer after initial load or after appending new items
				if (this.isMobileViewport) {
					this.$nextTick(() => {
						this.setupIntersectionObserver();
					});
				}
			} catch (error) {
				// show empty moments
			} finally {
				this.isLoading = false;
			}
		},
		setupIntersectionObserver() {
			// Clean up existing observer if it exists
			if (this.observer) {
				this.observer.disconnect();
			}

			this.observer = new IntersectionObserver(
				(entries) => {
					const isIntersecting = entries[0].isIntersecting;

					if (isIntersecting && !this.isLoading && this.videos.pagination.currentPage < this.videos.pagination.lastPage) {
						this.loadMoreVideos();
					}
				},
				{
					rootMargin: '200px',
					threshold:  0.1,
				}
			);

			if (this.$refs.loadMoreTrigger) {
				this.observer.observe(this.$refs.loadMoreTrigger);
			}
		},
		async loadMoreVideos() {
			await this.fetchVideos(this.videos.pagination.currentPage + 1, true);
		},
		handleDownloadMoment(momentCrop) {
			window.eventBus.$emit(`modal:${this.modalNameMomentDownload}`, momentCrop);
		},
		handleDeleteMoment(momentCrop) {
			window.eventBus.$emit(`modal:${this.modalNameMomentDelete}`, momentCrop);
		},
		handlePaginationChange(page) {
			this.fetchVideos(page, false);
		},
		async handleMomentDeleteConfirm(momentCrop) {
			try {
				await httpClient.deleteEventVideoCrop(momentCrop.id);
				this.fetchVideos(1, false);
			} catch (error) {
				window.toast.error(this.$I18n.trans('errors.500.title'));
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.moments {
	// margin-top: $sp5;

	// @include size(md-up) {
	// 	margin-top: $sp7;
	// }
}

.moments-introduction {
	margin-bottom: $sp6;
	margin-left: auto;
	margin-right: auto;
	padding-top: $sp5;
	max-width: 800px;
	text-align: center;

	@include size(md-up) {
		margin-bottom: $sp7;
		padding-top: 0;
	}
}

.moments-introduction__title {
	@include font(primary, light, normal, fs-150);

	margin-bottom: $sp4;
	color: var(--color-white);

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-black);
	}
}

.moments-introduction__description {
	@include font(primary, light, normal, fs-100);

	margin-bottom: 0;
	color: var(--color-grey-300);

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-grey-700);
	}
}

.moments__list {
	position: relative;
	display: grid;
	grid-gap: $sp4;
	margin-bottom: $sp5;

	@include size(sm-up) {
		grid-template-columns: repeat(2, 1fr);
		grid-gap: $sp4;
	}

	@include size(md-up) {
		grid-template-columns: repeat(5, 1fr);
	}
}

.moments__loading-spinner {
	position: absolute;
	display: flex;
	align-items: center;
	top: 50%;
	left: 50%;
	transform: translateX(-50%) translateY(-50%);
}

.moments__load-more-trigger {
	height: 1px;
	width: 100%;
}

.moments__instructions {
	position: relative;
	display: grid;
	grid-template-columns: repeat(1, 1fr);
	gap: $sp4;
	padding: $sp6;
	max-width: 1040px;
	margin-bottom: $sp5;
	margin-left: auto;
	margin-right: auto;
	border-radius: $border-radius--sm;
	align-items: center;
	justify-content: center;

	&.is-mobile {
		grid-template-columns: 1fr;
		padding: $sp6 $sp4;
	}

	@include size(md-up) {
		grid-template-columns: repeat(3, 1fr);
		margin-bottom: $sp8;

		&::before,
		&::after {
			content: '';
			display: block;
			position: absolute;
			left: $sp6;
			right: $sp6;
			height: 1px;
			background-color: var(--color-grey-600);
		}

		&::before {
			top: 0;
		}

		&::after {
			bottom: 0;
		}
	}
}

.moments__instruction-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 0 $sp2;
	min-height: 240px;
	justify-content: flex-start;

	.moments__instructions.is-mobile & {
		min-height: 220px;
	}
}

.moments__instruction-number {
	font-size: 100px; // stylelint-disable-line
	font-weight: 600; // stylelint-disable-line
	line-height: 1; // stylelint-disable-line
	color: var(--color-grey-600);
	margin-bottom: $sp5;

	.moments__instructions.is-mobile & {
		margin-bottom: $sp4;
		min-width: 80px;
	}
}

.moments__instruction-content {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	max-width: 100%;
	text-align: center;
}

.moments__instruction-title {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: $sp2;
	font-size: 18px; // stylelint-disable-line
	margin-bottom: $sp4;
	color: var(--color-white);

	body.-t-light & { // stylelint-disable-line
		color: var(--color-black);
	}
}

.moments__instruction-text {
	font-size: 14px; // stylelint-disable-line
	color: var(--color-grey-300);
	margin: 0;
	line-height: 1.4; // stylelint-disable-line
	text-align: center;
	max-width: 240px;
}

.moments__pagination {
	margin-top: $sp5;
	margin-bottom: $sp5;
}
</style>
